import React from 'react';
import PropTypes from 'prop-types';

/**
 * Button - A wrapper that renders a standard HTML button with Bulma class names
 * 
 * This component accepts the same props as Bloomer Button for easy migration
 * If href is provided, it will render as an anchor tag instead of a button
 */
const Button = ({
  className = '',
  isColor = null,
  isSize = null,
  isOutlined = false,
  isInverted = false,
  isStatic = false,
  isRounded = false,
  isLoading = false,
  isActive = false,
  isFocused = false,
  isHovered = false,
  isText = false,
  isDisabled = false,
  isFullWidth = false,
  hasTextColor = null,
  children = null,
  onClick = () => {},
  style = {},
  type = 'button',
  href = null,
  tag = null,
  ...props
}) => {
  // Build the class names based on props
  const getClassNames = () => {
    const classes = ['button'];
    
    // Add color classes
    if (isColor) {
      classes.push(`is-${isColor}`);
    }
    
    // Add text color if specified
    if (hasTextColor) {
      classes.push(`has-text-${hasTextColor}`);
    }
    
    // Add size classes
    if (isSize) {
      classes.push(`is-${isSize}`);
    }
    
    // Add modifier classes
    if (isOutlined) {
      classes.push('is-outlined');
    }
    if (isInverted) {
      classes.push('is-inverted');
    }
    if (isRounded) {
      classes.push('is-rounded');
    }
    if (isLoading) {
      classes.push('is-loading');
    }
    if (isActive) {
      classes.push('is-active');
    }
    if (isFocused) {
      classes.push('is-focused');
    }
    if (isHovered) {
      classes.push('is-hovered');
    }
    if (isText) {
      classes.push('is-text');
    }
    if (isFullWidth) {
      classes.push('is-fullwidth');
    }
    
    // Add custom class names
    if (className) {
      classes.push(className);
    }
    
    return classes.join(' ');
  };

  // Determine the element type - use anchor if href is provided, or custom tag, otherwise button
  const Element = href ? 'a' : (tag || 'button');
  
  // Prepare props based on element type
  const elementProps = {
    className: getClassNames(),
    style,
    ...props,
  };
  
  if (Element === 'a') {
    // For anchor tags
    elementProps.href = href;
    elementProps.onClick = onClick;
    if (isDisabled || isStatic) {
      elementProps.onClick = (e) => e.preventDefault();
      elementProps.style = { ...style, pointerEvents: 'none', opacity: 0.5 };
    }
  } else {
    // For button tags
    elementProps.type = type;
    elementProps.disabled = isDisabled || isStatic;
    elementProps.onClick = onClick;
  }

  return (
    <Element {...elementProps}>
      {isLoading ? 'Loading...' : children}
    </Element>
  );
};

Button.propTypes = {
  className: PropTypes.string,
  isColor: PropTypes.string,
  isSize: PropTypes.string,
  isOutlined: PropTypes.bool,
  isInverted: PropTypes.bool,
  isStatic: PropTypes.bool,
  isRounded: PropTypes.bool,
  isLoading: PropTypes.bool,
  isActive: PropTypes.bool,
  isFocused: PropTypes.bool,
  isHovered: PropTypes.bool,
  isText: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  hasTextColor: PropTypes.string,
  onClick: PropTypes.func,
  style: PropTypes.object,
  type: PropTypes.string,
  href: PropTypes.string,
  tag: PropTypes.string,
  children: PropTypes.node,
};



export default Button;
